// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import iconsObject from "@insite/mobius/Icons/commonIcons";

import { setClipboardText } from "@hardwoods/App/Widgets/DesignSystem/DesignSystem.utils";
import xIconsObject from "@hardwoods/XDL/Icons/xngCommonIcons";
import { color } from "@hardwoods/XDL/Utilities";
import { xGap } from "@hardwoods/XDL/Utilities/SharedStyles";
import { ToastProps } from "@insite/mobius/Toast";
import Toaster from "@insite/mobius/Toast/Toaster";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React from "react";
import { css } from "styled-components";

const style = {
    container: {
        css: css`
            display: flex;
            ${xGap("20px")}
            flex-wrap: wrap;
            justify-content: center;
            padding: 15px;
        `,
    },
    codeSnippet: {
        css: css`
            background: #f4f4f4;
            border: 1px solid #ddd;
            border-left: 3px solid #f36d33;
            color: #666;
            page-break-inside: avoid;
            font-family: monospace;
            font-size: 15px;
            line-height: 1.6;
            max-width: 100%;
            overflow: auto;
            padding: 1em 1.5em;
            display: block;
            word-wrap: break-word;
        `,
    },
    iconDemo: {
        container: {
            css: css`
                text-align: center;
                border-radius: 8px;
                border: 1px solid ${getColor("common.border")};
                padding: 15px;
                overflow-wrap: break-word;
                min-width: 120px;
                min-height: 116px;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;

                background: ${color("common.white")};

                &:hover {
                    background: ${color("common.ui2", 0.6)};
                }
            `,
        },
        title: {
            size: 12,
            color: "text.accent",
            css: css`
                display: block;
            `,
        },
    },
};

const IconDemo = ({
    iconSrc,
    name,
    addToast,
}: {
    iconSrc: any;
    name: any;
    addToast: (toastProps: ToastProps) => void;
}) => (
    <Clickable
        onClick={() => {
            const isCopied = setClipboardText(name);
            addToast({
                body: isCopied ? `${name} icon is copied` : `copy ${name} icon failed, do it manually`,
                messageType: isCopied ? "success" : "danger",
            });
        }}
    >
        <StyledWrapper {...style.iconDemo.container}>
            <Icon
                src={iconSrc}
                css={css`
                    margin-bottom: 15px;
                    &,
                    & svg {
                        height: 32px;
                        width: initial;
                    }
                `}
            />
            <Typography {...style.iconDemo.title}>{name}</Typography>
        </StyledWrapper>
    </Clickable>
);

const importPath = "@hardwoods/XDL/Icons";

export const XdlIcons = () => {
    return (
        <>
            <Toaster position="bottom" mobilePosition="bottom">
                <ToasterContext.Consumer>
                    {({ addToast }) => (
                        <>
                            <Clickable
                                css={css`
                                    margin-left: 40px;
                                    margin-bottom: 15px;
                                `}
                                onClick={() => {
                                    const isCopied = setClipboardText(importPath);
                                    addToast({
                                        body: isCopied
                                            ? `${importPath} is copied`
                                            : `copy ${importPath} failed, do it manually`,
                                        messageType: isCopied ? "success" : "danger",
                                    });
                                }}
                            >
                                <Typography {...style.codeSnippet}>{importPath}</Typography>
                            </Clickable>

                            <StyledWrapper {...style.container}>
                                <Typography
                                    as="p"
                                    css={css`
                                        font-weight: bold;
                                        width: 100%;
                                    `}
                                >
                                    Hardwoods Icons
                                </Typography>

                                {/* Hardwoods icons */}
                                {Object.keys(xIconsObject).map(iconKey => {
                                    const src = xIconsObject[iconKey];
                                    return (
                                        <IconDemo iconSrc={src} name={iconKey} key={uniqueId()} addToast={addToast} />
                                    );
                                })}
                            </StyledWrapper>

                            <StyledWrapper {...style.container}>
                                <Typography
                                    as="p"
                                    css={css`
                                        font-weight: bold;
                                        width: 100%;
                                    `}
                                >
                                    Epi Icons
                                </Typography>

                                {/* Epi Icons */}
                                {Object.keys(iconsObject).map(iconKey => {
                                    const src = iconsObject[iconKey];
                                    return (
                                        <IconDemo iconSrc={src} name={iconKey} key={uniqueId()} addToast={addToast} />
                                    );
                                })}
                            </StyledWrapper>
                        </>
                    )}
                </ToasterContext.Consumer>
            </Toaster>
        </>
    );
};
